<template>
  <div
    class="rs-rate-input"
    :class="inputClasses"
  >
    <label
      v-if="label"
      :for="id"
    >{{ label }}
      <sup
        v-if="required"
        class="mt-2"
      >*</sup>
    </label>
    <ValidationProvider
      v-slot="{ errors }"
      class="veeValidate"
      tag="div"
      :name="label ? label : name ? name : 'field'"
      mode="aggressive"
      :rules="rules"
    >
      <div
        v-if="imported"
        class="import-tooltip"
        :class="{ 'import-tooltip--open': importHover }"
      >
        <p>This field is managed by <strong>[Data Source]</strong>.<br>Changing this will be temporary.</p>
      </div>
      <BInputGroup>
        <BInputGroupPrepend class="prepend">
          <span class="dollarSign prepend bg-1">
            $
          </span>
        </BInputGroupPrepend>
        <input
          :id="id"
          v-model="input"
          type="text"
          class="form-control"
          :max="max"
          :min="min"
          :step="step"
          :name="name"
          :required="required"
          :disabled="disabled || (imported && isUnlocked == false)"
          :placeholder="placeholder"
          :readonly="readonly || (imported && isUnlocked == false)"
          @change="onInputChange($event.target.value)"
          @keypress="ignoreNonNumericKeypress"
          @keypress.enter="handleEnter"
          @blur="(data) => $emit('blur', data)"
          @focus="(data) => $emit('focus', data)"
        >
      </BInputGroup>
      <div
        v-if="errors.length || customErrorMessage"
        class="text--error"
      >
        {{ errors[0] || customErrorMessage }}
      </div>
    </ValidationProvider>
  </div>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import { ValidationProvider, extend } from 'vee-validate';

extend('floatNumbers', {
  validate: (value) => {
    let trimValue = value;
    if (typeof (value) === 'string') {
      trimValue = trimValue.replace(/\s/g, '');
      if (trimValue === '') return true;
    }
    if (trimValue < 1) {
      return false;
    }
    return /^(?![.,]?$)\d*[.,]?\d{0,2}$/im.test(trimValue);
  },
  message: 'Input value should be numeric or greater than 0',
});

export default defineComponent({
  name: 'RsFormRateInput',
  components: {
    ValidationProvider,
  },
  props: {
    rules: [String, Object],
    id: {
      type: String,
      default: '',
    },
    label: String,
    placeholder: String,
    required: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    listingScoresHeart: {
      type: Boolean,
      default: false,
    },
    customErrorMessage: {
      type: String as () => string,
      required: false,
    },
    min: {
      type: Number,
    },
    max: {
      type: Number,
    },
    step: {
      type: Number,
    },
    wrap: {
      type: Boolean,
      default: false,
    },
    value: {
      type: [String, Number],
      default: '',
    },
    imported: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String,
      default: 'input',
    },
    /**
     * Color variants:  success | warning | error | error-alt
     *
     */
    variant: {
      type: String,
      validator: (prop: string) => [
        'success',
        'warning',
        'error',
        'error-alt',
      ].includes(prop),
    },
  },
  data() {
    return {
      isUnlocked: false as boolean,
      importHover: false as boolean,
      input: this.value as string | number,
    };
  },
  computed: {
    inputClasses(): Record<string, boolean> {
      return {
        'rs-input--success': this.variant === 'success',
        'rs-input--warning': this.variant === 'warning',
        'rs-input--error': this.variant === 'error',
        'rs-input--error-alt': this.variant === 'error-alt',
        'rs-input--imported': this.imported,
        'rs-input--unlocked': this.isUnlocked,
      };
    },
  },
  watch: {
    input() {
      if (this.input !== 'NaN') {
        this.$emit('input', this.input);
      }
    },
    value() {
      if (this.value !== 'NaN') {
        this.input = this.value;
      }
    },

  },
  methods: {
    onInputChange(val: string): void {
      if (val !== '') {
        this.input = val;
        this.$emit('input', this.input);
        this.$forceUpdate();
      } else {
        this.$emit('input', null);
      }
    },
    ignoreNonNumericKeypress(e: KeyboardEvent): boolean {
      const evt = (e) || window.event;
      const charCode = (evt.which ? evt.which : evt.keyCode);

      if ((charCode > 31 && (charCode < 48 || charCode > 57)) && charCode !== 46) {
        evt.preventDefault();
        return false;
      }
      return true;
    },
    toggleState(): void {
      this.isUnlocked = !this.isUnlocked;
    },
    handleEnter(data: Event): void {
      this.$emit('enter', data);
    },
  },
});
</script>

<style lang="scss">
@import '@/styles/_variables.scss';
.rs-rate-input {
  .prepend {
    width: 41px;
    height: 40px;
  }
  .input {
    padding: $input-padding;
    width: 100%;
    height: 40px;
    background-color: $white;
    line-height: $input-line-height;
    border-radius: $input-radius;
    border: $input-border;
    color: $input-color;
    font-size: $input-font-size;
    box-sizing: border-box;
    &:focus {
      box-shadow: 0 0 0 3px rgba($info, 0.1);
      border-color: $info;
    }
    @include placeholder {
      color: rgba($dark-gray, 0.4);
    }
  }
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  /* Firefox */
  input[type='number'] {
    -moz-appearance: textfield;
    appearance: textfield;
  }
  .dollarSign {
    transition: all 0.2s ease;
    background-color: $white;
    font-size: $form-label-font-size;
    display: flex;
    font-weight: $form-label-font-weight;
    border-radius: $input-radius 0px 0px $input-radius;
    border: $input-border;
    align-items: center;
    justify-content: center;
  }
}
</style>
